import { DateTime } from 'luxon';

import type { CreditReportAggregates, CreditScoreHistoryType } from './types';

type DateValue = {
  date: string;
  score: number | null;
  timestamp: number;
  delta: number;
};

/**
 * Generates an array of dates and credit score values.
 * @param creditScoreHistory The history of credit scores as an array.
 * @returns An array of objects with date, score, timestamp and delta properties.
 **/

export const generateScoreDeltaTimeline = (creditScoreHistory: CreditScoreHistoryType[] = []): DateValue[] => {
  // Ensure creditScoreHistory is sorted by refreshDate
  const sortedHistory = creditScoreHistory.sort(
    (a, b) => DateTime.fromISO(a.refreshDate).toMillis() - DateTime.fromISO(b.refreshDate).toMillis(),
  );

  const result: DateValue[] = [];
  let lastScore: number | null = null; // To keep track of the last known score

  sortedHistory.forEach((entry) => {
    const parsedDate = DateTime.fromISO(entry.refreshDate);
    const dateFormatted = parsedDate.toFormat('MM/dd/yyyy');
    const timestamp = parsedDate.toMillis();
    let delta = 0;

    if (lastScore !== null) {
      delta = entry.score - lastScore;
    }
    lastScore = entry.score; // Update last known score

    result.push({
      date: dateFormatted,
      score: entry.score,
      timestamp,
      delta,
    });
  });

  return result;
};

/** Reads one value of a score ingredient by name; undefined when the report lacks the ingredient or value. */
export const getIngredientValue = (
  aggregates: CreditReportAggregates | undefined,
  ingredient: keyof CreditReportAggregates,
  key: string,
): number | string | undefined => {
  const values: Record<string, unknown> | undefined = aggregates?.[ingredient];
  const value = values?.[key];
  return typeof value === 'number' || typeof value === 'string' ? value : undefined;
};
